import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError, fileUrl } from "@/lib/api";

export const GalleryManager = () => {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/admin/gallery");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a photo or video first.");
    setBusy(true);
    const body = new FormData();
    body.append("file", file);
    body.append("caption", caption);
    try {
      await api.post("/admin/gallery", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded and published to the Gallery page.");
      setCaption("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Upload failed."));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, payload, message) => {
    await api.patch(`/admin/gallery/${id}`, payload);
    toast.success(message);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Deleted. It is no longer on the website.");
    load();
  };

  const move = async (index, direction) => {
    const next = [...items];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    await api.post("/admin/gallery/reorder", { ids: next.map((i) => i.id) });
    toast.success("Order saved.");
  };

  return (
    <div className="space-y-8" data-testid="admin-gallery-manager">
      <form onSubmit={upload} className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-5">
        <div>
          <h2 className="text-lg font-medium text-slate-900">Add a photo or video</h2>
          <p className="text-sm text-slate-500 mt-1">
            Photos: JPG, PNG or WEBP up to 10 MB. Videos: MP4 or MOV up to 100 MB. New items are published
            straight away.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="gallery-file">Choose file</Label>
            <Input
              id="gallery-file"
              type="file"
              ref={fileRef}
              accept=".jpg,.jpeg,.png,.webp,.mp4,.mov"
              data-testid="admin-gallery-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="rounded-md"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gallery-caption">Caption (shown under the photo)</Label>
            <Input
              id="gallery-caption"
              value={caption}
              placeholder="e.g. Our sterilization room"
              data-testid="admin-gallery-caption-input"
              onChange={(e) => setCaption(e.target.value)}
              className="rounded-md"
            />
          </div>
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="admin-gallery-upload-btn"
          className="h-11 rounded-md bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
        >
          <Upload className="w-4 h-4 mr-2" />
          {busy ? "Uploading…" : "Upload & Publish"}
        </Button>
      </form>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-medium text-slate-900">Gallery items ({items.length})</h2>
          <p className="text-sm text-slate-500 mt-1">
            Use the arrows to change the order shown on the website.
          </p>
        </div>
        {items.length === 0 ? (
          <p className="p-6 text-sm text-slate-500" data-testid="admin-gallery-empty">
            Nothing uploaded yet.
          </p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {items.map((item, index) => (
              <li
                key={item.id}
                data-testid={`admin-gallery-row-${item.id}`}
                className="p-4 sm:p-6 flex flex-wrap items-center gap-5"
              >
                {item.media_type === "image" ? (
                  <img
                    src={fileUrl(item.storage_path)}
                    alt={item.caption || "Gallery item"}
                    className="w-24 h-20 object-cover rounded-md border border-slate-200"
                  />
                ) : (
                  <video
                    src={fileUrl(item.storage_path)}
                    className="w-24 h-20 object-cover rounded-md border border-slate-200 bg-slate-900"
                    preload="metadata"
                    muted
                  />
                )}
                <div className="flex-1 min-w-[180px]">
                  <input
                    defaultValue={item.caption}
                    data-testid={`admin-gallery-caption-${item.id}`}
                    onBlur={(e) => {
                      if (e.target.value !== item.caption)
                        patch(item.id, { caption: e.target.value }, "Caption saved.");
                    }}
                    className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    placeholder="Add a caption"
                  />
                  <p className="text-xs text-slate-400 mt-2">
                    {item.media_type} · {(item.size / (1024 * 1024)).toFixed(1)} MB ·{" "}
                    {item.published ? "Published" : "Hidden"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-md"
                    onClick={() => move(index, -1)}
                    data-testid={`admin-gallery-up-${item.id}`}
                    aria-label="Move up"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-md"
                    onClick={() => move(index, 1)}
                    data-testid={`admin-gallery-down-${item.id}`}
                    aria-label="Move down"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-md"
                    data-testid={`admin-gallery-toggle-${item.id}`}
                    onClick={() =>
                      patch(
                        item.id,
                        { published: !item.published },
                        item.published ? "Hidden from the website." : "Published to the website."
                      )
                    }
                  >
                    {item.published ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    {item.published ? "Unpublish" : "Publish"}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-md text-red-600 hover:bg-red-50"
                    onClick={() => remove(item.id)}
                    data-testid={`admin-gallery-delete-${item.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
